import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProgress, saveProgress } from '../services/api';

import WizardLayout from '../components/wizard/WizardLayout';
import { computeStepStatuses } from '../components/wizard/stepStatus';
import StepWelcome from '../components/wizard/StepWelcome';
import StepContact from '../components/wizard/StepContact';
import StepAccountsBeneficiaries from '../components/wizard/StepAccountsBeneficiaries';
import StepRiskAwareness from '../components/wizard/StepRiskAwareness';
import StepInvestmentAlignment from '../components/wizard/StepInvestmentAlignment';
import StepGrowthSatisfaction from '../components/wizard/StepGrowthSatisfaction';
import StepInvestmentCare from '../components/wizard/StepInvestmentCare';
import StepPortfolioMonitoring from '../components/wizard/StepPortfolioMonitoring';
import StepReview from '../components/wizard/StepReview';
import StepScheduleMeeting from '../components/wizard/StepScheduleMeeting';

const STEPS = [
  { key: 'schedule_meeting', label: 'Schedule Meeting', icon: 'bi-calendar2-check' },
  { key: 'welcome', label: 'Welcome', icon: 'bi-house-heart' },
  { key: 'contact', label: 'Contact Info', icon: 'bi-person-vcard' },
  { key: 'accounts_beneficiaries', label: 'Accounts & Beneficiaries', icon: 'bi-bank' },
  { key: 'risk_awareness', label: 'Risk Awareness', icon: 'bi-shield-exclamation' },
  { key: 'investment_alignment', label: 'Investment Alignment', icon: 'bi-graph-up-arrow' },
  { key: 'growth_satisfaction', label: 'Growth Satisfaction', icon: 'bi-bar-chart-line' },
  { key: 'investment_care', label: 'Investment Care', icon: 'bi-heart-pulse' },
  { key: 'portfolio_monitoring', label: 'Portfolio Monitoring', icon: 'bi-eye' },
  { key: 'review', label: 'Review', icon: 'bi-check2-all' },
];

function getMeetingStatus(scheduleData) {
  if (!scheduleData || typeof scheduleData !== 'object') return 'not_scheduled';

  const explicitStatus = String(scheduleData.meeting_status || '').trim().toLowerCase();
  if (explicitStatus === 'canceled' || explicitStatus === 'cancelled') {
    return 'canceled';
  }
  if (explicitStatus === 'scheduled') {
    return 'scheduled';
  }

  return 'not_scheduled';
}

function hasScheduledMeeting(scheduleData) {
  const normalizedStatus = getMeetingStatus(scheduleData);
  if (normalizedStatus === 'canceled') return false;
  if (normalizedStatus === 'scheduled') return true;
  if (!scheduleData || typeof scheduleData !== 'object') return false;
  if (scheduleData.booking_detected) return true;

  const bookingInfo = scheduleData.booking_data?.bookingInfo;
  if (bookingInfo) {
    const statusCode = String(bookingInfo.statusCode || '').trim();
    if (statusCode === '1') return true;

    const meetingTimes = bookingInfo.MeetingTimes;
    if (Array.isArray(meetingTimes) && meetingTimes.length > 0) return true;
  }

  const bookedStatus = String(scheduleData.booking_data?.bookedstatus || '').toLowerCase();
  if (bookedStatus === 'canceled' || bookedStatus === 'cancelled') return false;
  if (bookedStatus === 'scheduled') return true;

  return false;
}

/**
 * WizardPage — main Annual Check-In controller.
 *
 * Loads saved progress on mount, renders the current step, and
 * auto-saves as the user navigates.
 */
export default function WizardPage() {
  const { client, logout } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [prefill, setPrefill] = useState(null);
  const [scheduleMeetingUrl, setScheduleMeetingUrl] = useState('');
  const [secondaryAdvisor, setSecondaryAdvisor] = useState('');
  const [clientType, setClientType] = useState('hff');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    async function load() {
      try {
        const data = await getProgress();
        setCurrentStep(data.current_step || 0);
        setFormData(data.form_data || {});
        setPrefill(data.prefill || null);
        setScheduleMeetingUrl(data.schedule_meeting_url || '');
        setSecondaryAdvisor(data.secondary_advisor || '');
        setClientType(data.client_type || 'hff');
      } catch (err) {
        console.error('Failed to load progress:', err);
        setError('Unable to load your saved progress. Please reload.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const persist = useCallback(
    async (step, data, completed = false) => {
      setSaving(true);
      try {
        await saveProgress(step, data, completed);
        setError(null);
        return true;
      } catch (err) {
        console.error('Save failed:', err);
        setError('Could not save — please check your connection.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  function goNext() {
    const currentKey = STEPS[currentStep]?.key;

    if (
      currentKey === 'schedule_meeting'
      && !hasScheduledMeeting(formData?.schedule_meeting)
    ) {
      setError('Please schedule your meeting before continuing.');
      return;
    }

    const nextStep = currentStep + 1;
    const isLast = currentStep === STEPS.length - 1;

    if (isLast) {
      if (!hasScheduledMeeting(formData?.schedule_meeting)) {
        setError('Please schedule your meeting before finishing.');
        return;
      }
      persist(currentStep, formData, true).then((ok) => {
        if (ok) navigate('/complete', { replace: true });
      });
      return;
    }

    setCurrentStep(nextStep);
    persist(nextStep, formData);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function goBack() {
    if (currentStep <= 0) return;
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    persist(prevStep, formData);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function jumpTo(idx) {
    if (idx === currentStep) return;

    const scheduleIdx = STEPS.findIndex((s) => s.key === 'schedule_meeting');
    if (
      idx > scheduleIdx
      && !hasScheduledMeeting(formData?.schedule_meeting)
    ) {
      setError('Please schedule your meeting before continuing.');
      return;
    }

    setCurrentStep(idx);
    persist(idx, formData);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function sectionData(key) {
    return formData[key] || {};
  }

  function updateSection(key) {
    return (updated) => {
      setFormData((prev) => ({ ...prev, [key]: updated }));
    };
  }

  function updateScheduleSection(updated) {
    setFormData((prev) => {
      const next = { ...prev, schedule_meeting: updated };
      persist(currentStep, next);
      return next;
    });
  }

  function renderStep() {
    const stepKey = STEPS[currentStep]?.key;
    switch (stepKey) {
      case 'schedule_meeting':
        return (
          <StepScheduleMeeting
            data={sectionData('schedule_meeting')}
            onChange={updateScheduleSection}
            schedulingUrl={scheduleMeetingUrl}
            formData={formData}
            prefill={prefill}
            clientName={client?.name || ''}
            secondaryAdvisor={secondaryAdvisor}
          />
        );
      case 'welcome':
        return <StepWelcome clientName={client?.name} />;
      case 'contact':
        return (
          <StepContact
            data={sectionData('contact')}
            prefill={prefill}
            onChange={updateSection('contact')}
          />
        );
      case 'accounts_beneficiaries':
        return (
          <StepAccountsBeneficiaries
            data={sectionData('accounts_beneficiaries')}
            onChange={updateSection('accounts_beneficiaries')}
          />
        );
      case 'risk_awareness':
        return (
          <StepRiskAwareness
            data={sectionData('risk_awareness')}
            onChange={updateSection('risk_awareness')}
          />
        );
      case 'investment_alignment':
        return (
          <StepInvestmentAlignment
            data={sectionData('investment_alignment')}
            onChange={updateSection('investment_alignment')}
          />
        );
      case 'growth_satisfaction':
        return (
          <StepGrowthSatisfaction
            data={sectionData('growth_satisfaction')}
            onChange={updateSection('growth_satisfaction')}
          />
        );
      case 'investment_care':
        return (
          <StepInvestmentCare
            data={sectionData('investment_care')}
            onChange={updateSection('investment_care')}
          />
        );
      case 'portfolio_monitoring':
        return (
          <StepPortfolioMonitoring
            data={sectionData('portfolio_monitoring')}
            onChange={updateSection('portfolio_monitoring')}
          />
        );
      case 'review':
        return <StepReview formData={formData} />;
      default:
        return <p>Unknown step</p>;
    }
  }

  const isRebelClient = clientType === 'rebellionaire';
  const themeClass = isRebelClient ? 'theme-rebellionaire' : '';

  if (loading) {
    return (
      <div className={`d-flex justify-content-center align-items-center vh-100 ${themeClass}`}>
        <div className="text-center">
          <div className="spinner-border text-danger mb-3" role="status" />
          <p className="text-muted">Loading your progress…</p>
        </div>
      </div>
    );
  }

  const stepStatuses = computeStepStatuses(STEPS, formData);

  return (
    <div className={themeClass}>
      {error && (
        <div
          className="alert alert-warning position-fixed bottom-0 start-50 translate-middle-x mb-4 shadow-sm"
          style={{ zIndex: 9999, maxWidth: 500 }}
        >
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      <WizardLayout
        currentStep={currentStep}
        steps={STEPS}
        onBack={goBack}
        onNext={goNext}
        onSkip={undefined}
        showSkip={false}
        isFirst={currentStep === 0}
        isLast={currentStep === STEPS.length - 1}
        nextDisabled={false}
        saving={saving}
        submitLabel="Submit"
        onStepClick={jumpTo}
        stepStatuses={stepStatuses}
        lockFutureSteps={!hasScheduledMeeting(formData?.schedule_meeting)}
        clientName={client?.name || ''}
        onLogout={logout}
      >
        {renderStep()}
      </WizardLayout>
    </div>
  );
}
